import { useCallback, useMemo, useRef, useState } from 'react';
import useUserPreferences from '../../../hooks/useUserPreferences';
import type { Column } from '../types';

type UseColumnDragReorderProps<T> = {
  columnsToShow: Column<T>[];
  localStorageKey?: string;
};

type UseColumnDragReorderReturn = {
  draggingColumnId: string | null;
  isReordering: boolean;
  columnTranslations: Record<string, number>;
  columnRefs: React.MutableRefObject<
    Record<string, HTMLTableCellElement | null>
  >;
  handleDragStart: (
    e: React.DragEvent<HTMLTableCellElement>,
    colId: string,
  ) => void;
  handleDragOver: (
    e: React.DragEvent<HTMLTableCellElement>,
    colId: string,
    isFirstColumn: boolean,
  ) => void;
  handleDrop: (e: React.DragEvent<HTMLTableCellElement>) => void;
  handleDragEnd: () => void;
};

const useColumnDragReorder = <T extends object>({
  columnsToShow,
  localStorageKey,
}: UseColumnDragReorderProps<T>): UseColumnDragReorderReturn => {
  const [draggingColumnId, setDraggingColumnId] = useState<string | null>(null);
  const [previewColumnOrder, setPreviewColumnOrder] = useState<string[] | null>(
    null,
  );
  const columnRefs = useRef<Record<string, HTMLTableCellElement | null>>({});

  const { userPreferences, updateUserPreferences } = useUserPreferences();

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLTableCellElement>, colId: string) => {
      setDraggingColumnId(colId);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', colId);
    },
    [],
  );

  const handleDragOver = useCallback(
    (
      e: React.DragEvent<HTMLTableCellElement>,
      colId: string,
      isFirstColumn: boolean,
    ) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';

      if (!draggingColumnId || colId === draggingColumnId) return;

      // Don't allow dragging over meta or frozen columns - they should stay in place
      const targetColumn = columnsToShow.find((c) => c.id === colId);
      if (targetColumn?.metaColumn || targetColumn?.sticky) return;

      // Determine which side of the column the cursor is on
      const rect = e.currentTarget.getBoundingClientRect();
      const cursorX = e.clientX;
      const columnMidpoint = rect.left + rect.width / 2;

      // Only insert before for the first column when cursor is on left half
      const side = isFirstColumn && cursorX < columnMidpoint ? 'left' : 'right';

      // Separate regular columns from meta columns (meta columns always stay at the end)
      const regularColumns = columnsToShow.filter((c) => !c.metaColumn);
      const metaColumns = columnsToShow.filter((c) => c.metaColumn);

      // Compute preview order only for regular columns
      const currentOrder = regularColumns.map((c) => c.id);
      const sourceIndex = currentOrder.indexOf(draggingColumnId);
      const targetIndex = currentOrder.indexOf(colId);

      if (sourceIndex !== -1 && targetIndex !== -1) {
        const newOrder = [...currentOrder];
        newOrder.splice(sourceIndex, 1);

        const adjustedTargetIndex =
          targetIndex > sourceIndex ? targetIndex - 1 : targetIndex;
        const insertIndex =
          side === 'left' ? adjustedTargetIndex : adjustedTargetIndex + 1;

        newOrder.splice(insertIndex, 0, draggingColumnId);

        // Append meta columns at the end (they always stay in their original order)
        const fullOrder = [...newOrder, ...metaColumns.map((c) => c.id)];
        setPreviewColumnOrder(fullOrder);
      }
    },
    [draggingColumnId, columnsToShow],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLTableCellElement>) => {
      e.preventDefault();

      // Persist the preview order if available
      if (previewColumnOrder && localStorageKey) {
        updateUserPreferences('columnOrder', {
          ...userPreferences.columnOrder,
          [localStorageKey]: previewColumnOrder,
        });
      }

      setDraggingColumnId(null);
      setPreviewColumnOrder(null);
    },
    [
      localStorageKey,
      updateUserPreferences,
      userPreferences.columnOrder,
      previewColumnOrder,
    ],
  );

  const handleDragEnd = useCallback(() => {
    setDraggingColumnId(null);
    setPreviewColumnOrder(null);
  }, []);

  // Get actual rendered column width from DOM
  const getColumnWidth = useCallback(
    (colId: string) => {
      const element = columnRefs.current[colId];
      if (element) {
        return element.getBoundingClientRect().width;
      }
      // Fallback to column definition width
      const col = columnsToShow.find((c) => c.id === colId);
      if (col && typeof col.width === 'number') return col.width;
      return 165; // default width
    },
    [columnsToShow],
  );

  // Calculate translation offsets for animated column reordering
  const columnTranslations = useMemo(() => {
    const translations: Record<string, number> = {};

    if (!previewColumnOrder || !draggingColumnId) {
      return translations;
    }

    const originalOrder = columnsToShow.map((c) => c.id);

    // Calculate cumulative positions for original order
    const originalPositions: Record<string, number> = {};
    let pos = 0;
    for (const colId of originalOrder) {
      originalPositions[colId] = pos;
      pos += getColumnWidth(colId);
    }

    // Calculate cumulative positions for preview order
    const previewPositions: Record<string, number> = {};
    pos = 0;
    for (const colId of previewColumnOrder) {
      previewPositions[colId] = pos;
      pos += getColumnWidth(colId);
    }

    // Calculate translation for each column
    for (const colId of originalOrder) {
      const originalPos = originalPositions[colId] || 0;
      const previewPos = previewPositions[colId] || 0;
      translations[colId] = previewPos - originalPos;
    }

    return translations;
  }, [previewColumnOrder, draggingColumnId, columnsToShow, getColumnWidth]);

  return {
    draggingColumnId,
    isReordering: !!draggingColumnId,
    columnTranslations,
    columnRefs,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  };
};

export default useColumnDragReorder;
